import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Plus } from "lucide-react";
import { userService } from "../../services/userService";
import { testrunService } from "../../services/testrunService";
import toast from "react-hot-toast";

const UNASSIGNED = "__unassigned__";

const AssignToModal = ({
  open,
  onClose,
  testCase,
  testRunId,
  projectId,
  onSuccess,
  isTestRunCompleted = false,
  assignees = [],
}) => {
  const [assigneeId, setAssigneeId] = useState(UNASSIGNED);
  const [comment, setComment] = useState("");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadUsers = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoadingUsers(true);
      const response = await userService.findUsers({ project_id: projectId });
      if (response.success) {
        setUsers(response.data?.users || []);
      }
    } catch (error) {
      console.error("Error loading users:", error);
    } finally {
      setLoadingUsers(false);
    }
  }, [projectId]);

  const handleSave = async () => {
    if (!testCase?.test_run_case_id || !testRunId || !projectId) return;
    if (isTestRunCompleted) {
      toast.error("Cannot change assignee on a completed test run");
      return;
    }

    try {
      setSaving(true);
      const assignedTo = assigneeId === UNASSIGNED ? null : assigneeId;
      await testrunService.updateAssignedTo(
        testRunId,
        testCase.test_run_case_id,
        assignedTo,
      );

      if (comment.trim()) {
        const base = (testCase.notes || "").trim();
        const addition = comment.trim();
        const notes = base ? `${base}\n\n${addition}` : addition;
        try {
          await testrunService.updateTestCaseExecution(
            testRunId,
            testCase.test_run_case_id,
            projectId,
            { notes },
          );
        } catch (noteErr) {
          console.error(noteErr);
          toast.error(
            noteErr.response?.data?.message ||
              "Assignee updated, but comment could not be saved",
          );
          onSuccess?.();
          onClose();
          return;
        }
      }

      toast.success("Assignee updated successfully");
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error updating assignee:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to update assignee",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader className="bg-[#a17561] text-white p-4 -m-6 mb-4 rounded-t-lg">
          <DialogTitle className="text-white text-xl font-bold">
            Assign To
          </DialogTitle>
          {testCase?.title && (
            <p className="text-md text-white font-normal text-center mt-2">
              {testCase.title}
            </p>
          )}
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="assignee-select">Assignee</Label>
            <div className="mt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <Select
                value={assigneeId}
                onValueChange={setAssigneeId}
                disabled={isTestRunCompleted || loadingUsers}
              >
                <SelectTrigger
                  id="assignee-select"
                  className="flex-1 min-w-0 bg-[#f9f7f5] border-stone-200"
                >
                  <SelectValue
                    placeholder={
                      loadingUsers ? "Loading users…" : "Select assignee"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                  {assignees.map((u) => {
                    const id = String(u.value);
                    const label = u.label;
                    return (
                      <SelectItem key={id} value={id}>
                        {label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <button
                type="button"
                className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md button-brown-inverted transition-colors whitespace-nowrap shrink-0"
                onClick={() => {
                  toast("Invite new user functionality coming soon");
                }}
              >
                <Plus className="w-4 h-4 shrink-0" />
                Invite New User
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="assign-comment">Comment</Label>
            <Textarea
              id="assign-comment"
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Optional note about this assignment (appended to execution notes)."
              disabled={isTestRunCompleted}
              className="mt-1 resize-none border-stone-200 bg-[#fafaf9]"
            />
          </div>
        </div>

        <DialogFooter className="mt-6 gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="border-red-500 text-red-600 hover:bg-red-50"
          >
            Cancel
          </Button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || isTestRunCompleted || loadingUsers}
            className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium button-brown disabled:opacity-50 disabled:pointer-events-none"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignToModal;
